import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import MobileLogistics from "../mobile/MobileLogistics";

import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import { motion, AnimatePresence } from "framer-motion";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import {
  Navbar,
  Footer,
  FloatingActions,
  ContactForm,
  SocialRow,
} from "../components/Shared";
// Detailed modal data matching the services array
const LOGISTICS_SERVICES_DATA = {
  customs: {
    title: "Customs Clearance",
    icon: "fa-file-invoice",
    description: "Seamless clearance for sea & air cargo from Sharjah Port Khalid, Jebel Ali, and Dubai Airport.",
    details: [
      "Full documentation processing with CID, MOFA, Health, and Municipality approvals.",
      "Customs duty assessment & tariff classification to optimize import costs.",
      "Dedicated customs brokers operating 24/7 at major UAE entry ports.",
      "Swift clearance to prevent demurrage and port storage charges."
    ]
  },
  seafreight: {
    title: "Sea Freight",
    icon: "fa-ship",
    description: "FCL, LCL, Break Bulk and RORO with best rates from China, Pakistan, India to the UAE and worldwide.",
    details: [
      "Weekly departures linking major international hubs.",
      "Flexible options: Full Container Load (FCL) & Less than Container Load (LCL).",
      "Specialized handling for oversized, project, or roll-on/roll-off (RORO) cargo.",
      "End-to-end container tracking from port-of-origin to port-of-delivery."
    ]
  },
  airfreight: {
    title: "Air Freight",
    icon: "fa-plane",
    description: "DG and NDG cargo handling from UAE to Europe, USA, Canada, Australia, India, Pakistan and beyond.",
    details: [
      "Express, standard, and deferred air charter options.",
      "Certified handling for Dangerous Goods (DG) & Non-Dangerous Goods (NDG).",
      "Door-to-door, airport-to-airport, and port-to-door delivery networks.",
      "Strong alliances with premier global airlines for guaranteed cargo space."
    ]
  },
  landfreight: {
    title: "Land Freight",
    icon: "fa-truck",
    description: "Trailers (12m/15m), Flat Bed, Box, Curtain Side, Heavy Low beds & Pickups across UAE, Oman, KSA, Qatar, Bahrain, Kuwait & Jordan.",
    details: [
      "Cross-border land transportation across the GCC network.",
      "Diverse fleet: Flatbeds, curtain-siders, box trucks, and heavy low-bed options.",
      "Real-time GPS fleet tracking and daily transit status updates.",
      "Customs clearance assistance at all Middle Eastern border checkpoints."
    ]
  },
  express: {
    title: "Express Courier",
    icon: "fa-box-open",
    description: "Door-to-door fast & efficient export and import services worldwide with guaranteed transit times.",
    details: [
      "Time-sensitive parcel delivery with priority routing.",
      "Simplified custom procedures for fast-track import/export packages.",
      "Complete end-to-end tracking visibility with signature verification.",
      "Optimized solutions for e-commerce fulfillment and commercial samples."
    ]
  },
  warehousing: {
    title: "Warehousing & 3PL",
    icon: "fa-warehouse",
    description: "Cost-effective warehousing, repacking, distribution and integrated 3PL solutions for seamless flow of goods.",
    details: [
      "Secure storage facilities strategically positioned near UAE's key transport hubs.",
      "Value-added services: professional repacking, labeling, and sorting.",
      "Inventory control management with customized distribution frameworks.",
      "Seamless integration with our freight divisions for swift, end-to-end delivery."
    ]
  }
};

const services = [
  {
    id: "customs",
    icon: "fa-file-invoice",
    title: "Customs Clearance",
    link: "#contact",
    desc: "Seamless clearance for sea & air cargo from Sharjah Port Khalid, Jebel Ali and Dubai Airport with CID, MOFA, Health & Municipality approvals."
  },
  {
    id: "seafreight",
    icon: "fa-ship",
    title: "Sea Freight",
    link: "#contact",
    desc: "FCL, LCL, Break Bulk and RORO with best rates from China, Pakistan, India to the UAE and worldwide."
  },
  {
    id: "airfreight",
    icon: "fa-plane",
    title: "Air Freight",
    link: "#contact",
    desc: "DG and NDG cargo handling from UAE to Europe, USA, Canada, Australia, India, Pakistan and beyond."
  },
  {
    id: "landfreight",
    icon: "fa-truck",
    title: "Land Freight",
    link: "#contact",
    desc: "Trailers (12m/15m), Flat Bed, Box, Curtain Side, Heavy Low beds & Pickups across UAE, Oman, KSA, Qatar, Bahrain, Kuwait & Jordan."
  },
  {
    id: "express",
    icon: "fa-box-open",
    title: "Express Courier",
    link: "#contact",
    desc: "Door-to-door fast & efficient export and import services worldwide with guaranteed transit times."
  },
  {
    id: "warehousing",
    icon: "fa-warehouse",
    title: "Warehousing & 3PL",
    link: "#contact",
    desc: "Cost-effective warehousing, repacking, distribution and integrated 3PL solutions for seamless flow of goods."
  },
];

const features = [
  "Expert Customs Clearance",
  "Global Network Coverage",
  "Competitive Pricing",
  "Express Courier Services",
  "Specialised Stock Consolidation",
  "Dedicated Account Manager",
];



// =========================================================================
// TWO-COLUMN JCTRANS PREMIUM CERTIFICATION COMPONENT
// =========================================================================
function Certification() {
  return (
    <section className="relative py-20 overflow-hidden bg-gradient-to-br from-white via-blue-50/40 to-white border-t border-b border-blue-100/60">
      {/* Soft layout background blurs */}
      <div className="absolute top-[-100px] right-[-100px] w-[350px] h-[350px] bg-blue-200/20 rounded-full blur-[100px] pointer-events-none" />
      <div className="absolute bottom-[-100px] left-[-100px] w-[350px] h-[350px] bg-cyan-200/20 rounded-full blur-[100px] pointer-events-none" />

      <div className="max-w-[1380px] mx-auto px-5 md:px-6 relative z-10">
        <div className="grid lg:grid-cols-[45%_55%] gap-12 lg:gap-16 items-center">
          
          {/* LEFT DIVISION: Enlarged Logo, Badge & Text */}
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-5">
              {/* Enlarged JCTRANS Logo Container */}
              <div className="w-48 md:w-56 h-auto transition-transform duration-300 hover:scale-105">
                <img 
                  src="/updated-logo.png" 
                  alt="JCTRANS Premium Member" 
                  className="w-full h-auto object-contain"
                />
              </div>
              
              {/* Status Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-700 font-bold text-xs md:text-sm self-start sm:self-center shadow-sm">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <i className="fas fa-check-circle" /> Approved JCTRANS Premium Member
              </div>
            </div>

            {/* Content Header Text */}
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-slate-950 leading-tight tracking-[-1px]">
              Global Recognition & 
              <span className="block bg-gradient-to-r from-blue-700 to-cyan-500 bg-clip-text text-transparent">
                Freight Logistics Trust
              </span>
            </h2>

            <p className="text-slate-600 leading-relaxed text-base md:text-lg font-medium">
              Bajabar Incorporation FZE is proudly recognized as a JCTRANS Premium Member, demonstrating our commitment to trusted international freight forwarding, professional logistics services, and global business excellence.
            </p>

            {/* Metadata / Trust Indicators */}
            <div className="pt-4 space-y-3">
              <div className="flex items-center gap-3 text-slate-700 font-semibold text-sm">
                <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center">
                  <i className="fas fa-id-card text-sm" />
                </div>
                <span>Member ID: 161302</span>
              </div>
              <div className="flex items-center gap-3 text-slate-700 font-semibold text-sm">
                <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center">
                  <i className="fas fa-calendar-alt text-sm" />
                </div>
                <span>Valid Term: 2026.07 - 2027.06</span>
              </div>
            </div>

            {/* Helper Text */}
            <div className="pt-4 border-t border-slate-100">
              <h3 className="font-bold text-slate-900 text-base">
                Official Membership Certificate
              </h3>
              <p className="text-slate-500 text-xs mt-1.5 flex items-center gap-2">
                <i className="fas fa-search-plus text-blue-600 animate-bounce" /> 
                Click anywhere on the certificate image to view the full-size document.
              </p>
            </div>
          </div>

          {/* RIGHT DIVISION: Clickable Certificate with hover effect */}
          <div className="relative group">
            <div className="absolute inset-0 bg-blue-600/10 rounded-[32px] blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
            
            <a 
              href="/certificate.png" 
              target="_blank" 
              rel="noopener noreferrer"
              className="block relative bg-white border border-blue-100 rounded-[32px] p-4 md:p-6 shadow-[0_20px_50px_rgba(37,99,235,0.06)] group-hover:shadow-[0_30px_70px_rgba(37,99,235,0.15)] group-hover:border-blue-300 transition-all duration-500 hover:-translate-y-1.5"
            >
              <div className="relative overflow-hidden rounded-[24px] bg-slate-50 flex items-center justify-center aspect-[4/3] border border-slate-100">
                <img 
                  src="/certificate.png" 
                  alt="Bajabar JCTRANS Membership Certificate" 
                  className="max-h-full max-w-full object-contain transition-transform duration-500 group-hover:scale-[1.02]"
                />
                
                <div className="absolute inset-0 bg-slate-950/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <span className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-white text-slate-900 font-bold text-sm shadow-xl transform translate-y-4 group-hover:translate-y-0 transition-all duration-300">
                    <i className="fas fa-expand-arrows-alt text-blue-700" /> View Full Certificate
                  </span>
                </div>
              </div>
            </a>
          </div>

        </div>
      </div>
    </section>
  );
}
// =========================================================================
// TEAM SECTION COMPONENT
// =========================================================================
export function TeamSection() {
  const teamMembers = [
    {
      name: "Zubair Bin Mohammed Bajabar",
      role: "Managing Director",
      image: "/team-member1.png",
      email: "#",
      linkedin: "https://www.linkedin.com/in/zubair-bajajabar-772b03aa",
    },
    {
      name: "Muhammad Ahsan",
      role: "Operation And Admin Head",
      image: "/team-member3.png",
      email: "#",
      linkedin: "https://www.linkedin.com/in/muhammad-ahsan-411a3a8b",
    },
    {
      name: "Maz Pasha",
      role: "Assistant Operations Manager",
      image: "/team-member4.png",
      email: "#",
      linkedin: "#",
    },
    {
      name: "Ghouse Chida",
      role: "Accounts Manager",
      image: "/team-member2.png",
      email: "#",
      linkedin: "#",
    },
  ];

  return (
    <section className="py-20">
      {/* Header */}
      <div className="text-center mb-14">
        <span className="inline-flex items-center px-5 py-2 rounded-full bg-blue-50 text-blue-700 font-bold tracking-[0.2em] uppercase text-xs border border-blue-100">
          Our Team
        </span>

        <h2 className="mt-5 text-4xl md:text-5xl font-black text-slate-900">
          Meet Our Leadership
        </h2>

        <p className="mt-4 max-w-3xl mx-auto text-slate-500 text-base md:text-lg leading-8">
          Dedicated professionals delivering reliable logistics, customs
          clearance, freight forwarding and global trade solutions with
          operational excellence.
        </p>
      </div>

      {/* Team Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">

        {teamMembers.map((member) => (
          <div
            key={member.name}
            className="group bg-white rounded-3xl overflow-hidden border border-slate-200 shadow-md hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 flex flex-col"
          >

            {/* Image */}
            <div className="relative h-[340px] overflow-hidden bg-slate-100">
              <img
                src={member.image}
                alt={member.name}
                className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
              />
            </div>

            {/* Content */}
            <div className="flex-1 flex flex-col px-6 py-6">

              <h3 className="text-center text-xl font-extrabold text-slate-900 leading-7 min-h-[60px] flex items-center justify-center group-hover:text-blue-700 transition-colors">
                {member.name}
              </h3>

              <p className="mt-3 text-center text-slate-500 text-[15px] leading-6 min-h-[48px] flex items-center justify-center">
                {member.role}
              </p>

              {/* Footer */}
              <div className="mt-auto pt-6 border-t border-slate-100 flex items-center justify-between">

                <span className="text-sm text-slate-400 font-medium">
                  Get in touch
                </span>

                <div className="flex items-center gap-3">

                  <a
                    href={member.email}
                    className="w-10 h-10 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center transition-all duration-300 hover:bg-blue-700 hover:text-white"
                    title="Email"
                  >
                    <i className="fas fa-envelope"></i>
                  </a>

                  <a
                    href={member.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-xl bg-slate-100 text-slate-600 flex items-center justify-center transition-all duration-300 hover:bg-blue-700 hover:text-white"
                    title="LinkedIn"
                  >
                    <i className="fab fa-linkedin-in"></i>
                  </a>

                </div>

              </div>

            </div>

          </div>
        ))}

      </div>
    </section>
  );
}
// =========================================================================
// MAIN LOGISTICS EXPORT
// =========================================================================
export default function Logistics() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [activeService, setActiveService] = useState(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (isMobile) {
    return <MobileLogistics />;
  }

  return (
    <div className="min-h-screen bg-white overflow-hidden antialiased">
      {/* FIXED NAVBAR */}
      <div className="fixed top-0 left-0 w-full z-[100]">
        <Navbar showProducts={false} showVertiv={false} showBrands={false} />
      </div>

      {/* HERO */}
      <section
        id="home"
        className="relative min-h-[72vh] overflow-hidden bg-cover bg-center flex items-center pt-28"
        style={{
          backgroundImage: "url('/backgroundlanding.png')",
          backgroundSize: "cover",
          backgroundPosition: "center center",
        }}
      >
        <div className="absolute inset-0 bg-white/78 backdrop-blur-[1px]" />
        <div className="absolute inset-0 bg-gradient-to-r from-white via-white/88 to-white/40" />

        <div className="absolute -top-24 -left-24 w-[650px] h-[650px] bg-blue-300/20 rounded-full blur-[130px]" />
        <div className="absolute bottom-[-180px] right-[-120px] w-[550px] h-[550px] bg-cyan-200/20 rounded-full blur-[130px]" />

        <div className="relative z-20 max-w-[1380px] mx-auto px-6 lg:px-8 py-12 lg:py-16 grid lg:grid-cols-[1fr_.95fr] gap-10 xl:gap-16 items-center w-full">
          <div>
            <h1 className="mt-8 font-black leading-[0.95] tracking-[-2px] text-slate-950 text-[40px] sm:text-[52px] md:text-[64px] lg:text-[74px]">
              Your Freight
              <span className="block bg-gradient-to-r from-blue-700 to-cyan-500 bg-clip-text text-transparent">
                & Logistics
              </span>
              <span className="block text-slate-900">Solutions</span>
            </h1>

            <p className="mt-6 text-base md:text-lg text-slate-600 leading-[1.9] max-w-3xl font-medium">
              Trusted freight forwarding, customs clearance, warehousing and global cargo solutions from SAIF Zone, Sharjah, UAE.
            </p>

            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href="#contact"
                className="group relative overflow-hidden inline-flex items-center gap-3 px-7 py-4 rounded-2xl bg-gradient-to-r from-blue-700 to-blue-600 text-white font-bold shadow-[0_10px_40px_rgba(37,99,235,0.30)] hover:scale-105 hover:shadow-[0_20px_60px_rgba(37,99,235,0.45)] transition-all duration-500 text-sm md:text-base"
              >
                <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-all duration-500" />
                <i className="fas fa-paper-plane relative z-10" />
                <span className="relative z-10">Request a Free Quote</span>
              </a>

              <a
                href="#services"
                className="group inline-flex items-center gap-3 px-7 py-4 rounded-2xl bg-white/80 backdrop-blur-xl border border-white text-slate-900 font-bold shadow-lg hover:bg-white hover:scale-105 transition-all duration-500 text-sm md:text-base"
              >
                <i className="fas fa-list text-blue-700 group-hover:rotate-12 transition duration-300" />
                Our Services
              </a>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-6 max-w-lg">
              {[
                ["15+", "Years Experience"],
                ["500+", "Global Clients"],
                ["100K+", "Shipments"],
              ].map(([n, l]) => (
                <div key={l} className="group">
                  <div className="text-3xl md:text-4xl font-black bg-gradient-to-r from-blue-700 to-cyan-500 bg-clip-text text-transparent group-hover:scale-110 transition duration-300">
                    {n}
                  </div>
                  <div className="mt-2 text-[10px] md:text-[11px] uppercase tracking-[0.22em] text-slate-500 font-semibold">
                    {l}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-5 xl:gap-6">
            {[
              { i: "fa-file-invoice", t: "Customs Clearance", s: "Fast & reliable customs handling" },
              { i: "fa-plane", t: "Air Freight", s: "Worldwide air cargo delivery" },
              { i: "fa-ship", t: "Sea Freight", s: "Global ocean freight solutions" },
              { i: "fa-truck", t: "Land Freight", s: "Cross-border transportation" },
            ].map((c) => (
              <div
                key={c.t}
                className="group relative overflow-hidden p-5 md:p-7 rounded-[28px] backdrop-blur-2xl border border-white/70 bg-white/55 shadow-[0_15px_45px_rgba(0,0,0,0.06)] hover:-translate-y-2 hover:shadow-[0_25px_60px_rgba(37,99,235,0.12)] transition-all duration-700"
              >
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition duration-700 bg-gradient-to-br from-white/40 to-transparent" />
                <div className="relative z-10 w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-gradient-to-br from-blue-700 to-cyan-500 text-white grid place-items-center text-lg md:text-xl shadow-[0_10px_30px_rgba(37,99,235,0.30)] group-hover:scale-110 group-hover:rotate-6 transition-all duration-500">
                  <i className={`fas ${c.i}`} />
                </div>
                <h3 className="relative z-10 mt-5 text-[20px] md:text-[22px] leading-tight font-black text-slate-900">
                  {c.t}
                </h3>
                <p className="relative z-10 mt-3 text-slate-600 text-sm md:text-[15px] leading-[1.8]">
                  {c.s}
                </p>
                <div className="absolute inset-0 rounded-[28px] border border-blue-400/0 group-hover:border-blue-400/20 transition-all duration-700" />
              </div>
            ))}
          </div>
        </div>
        <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-white to-transparent" />
      </section>

      {/* ABOUT */}
      <section className="max-w-[1380px] mx-auto px-5 md:px-6 py-20 md:py-28 scroll-mt-28" id="about">
        <div className="text-center">
          <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-blue-50 border border-blue-100 text-blue-700 font-bold uppercase tracking-[0.18em] text-xs md:text-sm">
            About Us
          </span>
        </div>

        <div className="text-center mb-12 md:mb-14">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-slate-900 leading-tight tracking-[-1px]">
            Professional Freight Forwarding
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-700 to-cyan-500">
              & Customs Clearance Solutions
            </span>
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-slate-600 text-base md:text-lg leading-[2]">
              BAJABAR INCORPORATION FZE is a trusted logistics and freight forwarding company based in SAIF Zone, Sharjah, UAE.
            </p>
            <p className="mt-5 text-slate-600 text-base md:text-lg leading-[2]">
              We specialise in customs clearance, air freight, sea freight, land transport, warehousing, and complete cargo solutions for global businesses.
            </p>
            <p className="mt-5 text-slate-600 text-base md:text-lg leading-[2]">
              Our strong network ensures fast, reliable, and cost-effective logistics services across international markets.
            </p>

            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {[
                "Customs Clearance Experts",
                "Global Freight Network",
                "Fast Cargo Handling",
                "15+ Years Experience",
              ].map((item) => (
                <div
                  key={item}
                  className="flex items-center gap-3 bg-white border border-blue-100 rounded-2xl p-4 shadow-sm"
                >
                  <div className="w-10 h-10 rounded-xl bg-blue-100 text-blue-700 grid place-items-center">
                    <i className="fas fa-check" />
                  </div>
                  <div className="font-semibold text-slate-700 text-sm md:text-base">
                    {item}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Expanded maximum width and forced wide aspect-ratio */}
<div className="relative w-full max-w-4xl lg:max-w-5xl mx-auto">
  {/* Glow effect */}
  <div className="absolute -top-10 -right-10 w-48 h-48 bg-blue-200/30 rounded-full blur-3xl pointer-events-none" />
  
  {/* Video Frame */}
  <div className="rounded-[32px] bg-[#2E5FA8] overflow-hidden shadow-2xl border border-blue-100 p-2 md:p-3">
    <video 
      controls 
      className="w-full aspect-video object-contain rounded-[24px] shadow-xl bg-black/20"
      onTimeUpdate={(e) => {
        if (e.target.currentTime >= 44) {
          e.target.currentTime = 0;
          e.target.play();
        }
      }}
    >
      <source src="/company-video.mp4" type="video/mp4" />
      Your browser does not support the video tag.
    </video>
  </div>
</div>
        </div>
        <TeamSection />
      </section>

      {/* SERVICES */}
      <section className="bg-blue-50/50 py-20 md:py-28 scroll-mt-28" id="services">
        <div className="max-w-[1380px] mx-auto px-5 md:px-6">
          <div className="text-center mb-12">
            <span className="bj-eyebrow">Our Services</span>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold mt-3 text-blue-950">
              End-to-End Logistics Services
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {services.map((s, i) => (
              <div
                key={s.title}
                className="bg-white rounded-3xl border border-blue-100 p-6 shadow-xl hover:-translate-y-2 transition-all duration-500 flex flex-col"
                style={{ animationDelay: `${i * 80}ms` }}
              >
                <div className="w-12 h-12 rounded-2xl bg-blue-700 text-white grid place-items-center text-xl">
                  <i className={`fas ${s.icon}`} />
                </div>

                <h3 className="font-display text-xl md:text-2xl font-bold mt-5 text-blue-950">
                  {s.title}
                </h3>

                <p className="mt-3 text-slate-600 text-sm leading-relaxed flex-grow">
                  {s.desc}
                </p>

                <button
                  onClick={() => setActiveService(LOGISTICS_SERVICES_DATA[s.id])}
                  className="mt-6 inline-flex items-center justify-center gap-2 w-full py-4 bg-gradient-to-r from-blue-700 to-cyan-500 text-white rounded-2xl font-bold hover:scale-[1.02] transition duration-300"
                >
                  Learn More <i className="fas fa-arrow-right text-xs" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TWO-COLUMN JCTRANS PREMIUM CERTIFICATE SECTION */}
      <Certification />

      {/* WHY US */}
      <section className="py-20 md:py-28 bg-white scroll-mt-28" id="why">
        <div className="max-w-[1380px] mx-auto px-5 md:px-6">
          <div className="grid lg:grid-cols-[42%_58%] gap-12 items-center">
            <div className="relative">
              <div className="relative rounded-[34px] overflow-hidden shadow-[0_35px_90px_rgba(0,0,0,0.15)]">
                <img
                  src="/backgroundlanding.png"
                  alt="Logistics"
                  className="w-full h-[500px] md:h-[580px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B1B3A]/70 via-[#0B1B3A]/10 to-transparent" />
                <div className="absolute top-5 right-5 bg-white rounded-[20px] shadow-2xl px-5 py-4 flex items-center gap-3 min-w-[240px] md:min-w-[280px]">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-600 to-cyan-500 text-white grid place-items-center text-lg">
                    <i className="fas fa-plane" />
                  </div>
                  <div>
                    <h3 className="text-lg md:text-xl font-bold text-slate-900 leading-tight">
                      Worldwide Logistics
                    </h3>
                    <p className="text-slate-500 text-xs md:text-sm">
                      Fast & Secure Delivery
                    </p>
                  </div>
                </div>

                <div className="absolute bottom-5 left-5 right-5 grid grid-cols-3 gap-3">
                  {[
                    ["15+", "YEARS"],
                    ["500+", "CLIENTS"],
                    ["100K+", "SHIPMENTS"],
                  ].map(([num, label]) => (
                    <div key={label} className="bg-white rounded-[16px] py-4 text-center shadow-xl">
                      <div className="text-2xl md:text-3xl font-black text-slate-900">
                        {num}
                      </div>
                      <div className="text-[10px] tracking-[0.25em] text-slate-500 mt-2 font-semibold">
                        {label}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <span className="inline-flex px-5 py-2 rounded-full bg-blue-50 text-blue-700 font-bold tracking-[0.2em] uppercase text-xs md:text-sm">
                Why Choose Us
              </span>

              <h2 className="mt-5 text-3xl sm:text-4xl md:text-5xl font-black text-slate-900 leading-tight tracking-[-1px]">
                Trusted Global Logistics
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-700 to-cyan-500">
                  Solutions
                </span>
              </h2>

              <p className="mt-5 text-slate-600 leading-[1.9] text-base md:text-lg">
                We provide reliable freight forwarding, customs clearance and logistics solutions with strong operational excellence across UAE and global markets.
              </p>
              
              <div className="mt-8 grid sm:grid-cols-2 gap-y-4 gap-x-6">
                {features.map((f) => (
                  <div key={f} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center text-xs">
                      <i className="fas fa-check" />
                    </div>
                    <span className="text-slate-700 font-semibold text-sm md:text-base">{f}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section
        id="contact"
        className="scroll-mt-28 bg-gradient-to-b from-white to-blue-50 py-20 md:py-28"
      >
        <div className="max-w-[1380px] mx-auto px-5 md:px-6 grid lg:grid-cols-2 gap-14">

          {/* LEFT */}
          <div>

            <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-blue-50 border border-blue-100 text-blue-700 font-bold uppercase tracking-[0.18em] text-xs md:text-sm">
              Contact Us
            </span>

            <h2 className="mt-4 text-[38px] md:text-[56px] font-black text-blue-950">
              Send Your Inquiry
            </h2>

            <p className="mt-6 text-slate-600 text-lg leading-[1.9] max-w-lg">
              Reach out to our team for freight forwarding, customs clearance, land transport, warehousing, and global logistics solutions.
            </p>

            <div className="mt-10 grid sm:grid-cols-2 gap-5">

              <div className="bg-white rounded-3xl border border-blue-100 p-6 shadow-lg">

                <div className="font-bold text-blue-950 text-lg">
                  <i className="fas fa-user-tie text-blue-600 mr-2" />
                  Zubair Bin Mohammed
                </div>

                <div className="text-slate-500 mt-1">
                  Managing Director
                </div>

                <div className="mt-4 text-slate-700 leading-7">
                  zubair@bajabarinc.ae
                  <br />
                  +971 525131571
                </div>

              </div>

              <div className="bg-white rounded-3xl border border-blue-100 p-6 shadow-lg">

                <div className="font-bold text-blue-950 text-lg">
                  <i className="fas fa-user-tie text-blue-600 mr-2" />
                  Mohammed Ahsan
                </div>

                <div className="text-slate-500 mt-1">
                  Business Development
                </div>

                <div className="mt-4 text-slate-700 leading-7">
                  sales@bajabarinc.ae
                  <br />
                  +971 81252 11886
                </div>

              </div>

            </div>

            <div className="mt-10">
              <SocialRow />
            </div>

          </div>

          {/* FORM */}
          <div className="bg-white rounded-[40px] border border-blue-100 shadow-[0_25px_60px_rgba(0,0,0,0.08)] p-8 md:p-10">

            <h3 className="text-3xl font-black text-blue-950">
              Get In Touch
            </h3>

            <p className="mt-3 text-slate-600">
              Fill the form and our team will contact you shortly.
            </p>

            <div className="mt-8">
              <ContactForm
                division="logistics"
                services={[
                  "Customs Clearance",
                  "Sea Freight",
                  "Air Freight",
                  "Land Freight",
                  "Express Courier",
                  "Warehousing & 3PL",
                ]}
              />
            </div>

          </div>

        </div>

      </section>

      {/* FOOTER */}
      <Footer />
      <FloatingActions />

      {/* DETAILED SERVICES MODAL */}
      <AnimatePresence>
        {activeService && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] bg-slate-900/60 backdrop-blur-md flex items-center justify-center p-4 md:p-6"
            onClick={() => setActiveService(null)}
          >
            <motion.div 
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 20 }}
              className="bg-white rounded-[32px] overflow-hidden max-w-2xl w-full shadow-2xl border border-slate-100"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-8 md:p-10 relative">
                <button 
                  onClick={() => setActiveService(null)}
                  className="absolute top-6 right-6 w-10 h-10 rounded-full bg-slate-50 text-slate-500 hover:bg-slate-100 hover:text-slate-900 flex items-center justify-center transition"
                >
                  <i className="fas fa-times text-lg" />
                </button>

                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-blue-100 text-blue-700 flex items-center justify-center text-2xl">
                    <i className={`fas ${activeService.icon}`} />
                  </div>
                  <h3 className="text-2xl md:text-3xl font-black text-slate-900">
                    {activeService.title}
                  </h3>
                </div>

                <p className="mt-6 text-slate-600 leading-relaxed text-base md:text-lg">
                  {activeService.description}
                </p>

                <div className="mt-8 pt-8 border-t border-slate-100">
                  <h4 className="font-bold text-slate-900 text-sm uppercase tracking-wider mb-4">
                    Key Features &amp; Service Scope:
                  </h4>
                  <ul className="space-y-3">
                    {activeService.details.map((detail, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-slate-600 text-sm md:text-base leading-relaxed">
                        <div className="w-5 h-5 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center mt-1 flex-shrink-0 text-[10px]">
                          <i className="fas fa-check" />
                        </div>
                        <span>{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-10 flex gap-4">
                  <a
                    href="#contact"
                    onClick={() => setActiveService(null)}
                    className="flex-1 py-4 bg-gradient-to-r from-blue-700 to-cyan-500 text-white font-bold rounded-2xl text-center hover:shadow-[0_10px_25px_rgba(37,99,235,0.25)] transition duration-300"
                  >
                    Request Quote
                  </a>
                  <button
                    onClick={() => setActiveService(null)}
                    className="px-6 py-4 border border-slate-200 text-slate-600 font-bold rounded-2xl hover:bg-slate-50 transition"
                  >
                    Close
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}